import { useState, useEffect, useRef, useCallback } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

type PanelId = 'panel-fluency' | 'panel-retrieval' | 'panel-cognitive' | 'panel-difficulty' | 'panel-adaptation';

/**
 * "The Science" section -- four tabbed research panels powered by MD3 tabs.
 *
 * Each panel presents peer-reviewed evidence for the learning principles
 * Eduksy is built on. Bar-fill animations are staggered per bar when
 * a panel becomes active or the section first scrolls into view.
 */
export function ScienceSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });
  const tabsRef = useRef<HTMLElement | null>(null);
  const [activePanel, setActivePanel] = useState<PanelId>('panel-fluency');

  const reveal = (stagger?: number) =>
    `fade-up ${isVisible ? 'revealed' : ''}${stagger !== undefined ? '' : ''}`;

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  /** Map tab index to panel id. */
  const panelForIndex = useCallback((index: number): PanelId => {
    const panels: PanelId[] = ['panel-fluency', 'panel-retrieval', 'panel-cognitive', 'panel-difficulty', 'panel-adaptation'];
    return panels[index] ?? 'panel-fluency';
  }, []);

  /* Attach change listener to md-tabs web component. */
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;

    const handleChange = () => {
      const index: number = (tabs as any).activeTabIndex ?? 0;
      setActivePanel(panelForIndex(index));
    };

    tabs.addEventListener('change', handleChange);
    return () => tabs.removeEventListener('change', handleChange);
  }, [panelForIndex]);

  /* Animate bar fills inside the active panel when it changes or the section becomes visible. */
  useEffect(() => {
    if (!isVisible) return;

    const panel = document.getElementById(activePanel);
    if (!panel) return;

    const bars = panel.querySelectorAll<HTMLElement>('.bar-fill');
    const timers: ReturnType<typeof setTimeout>[] = [];

    bars.forEach((bar, index) => {
      bar.classList.remove('animated');
      const timer = setTimeout(() => {
        bar.classList.add('animated');
      }, 100 * index);
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [activePanel, isVisible]);

  const panelClass = (id: PanelId) =>
    `science-panel${activePanel === id ? ' active' : ''}`;

  return (
    <section
      id="science"
      className="section science-section"
      aria-label="The Science"
      ref={sectionRef}
    >
      <div className="section-inner">
        <h2 className={`section-title ${reveal()}`}>The Science</h2>
        <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
          Most platforms create the illusion of knowledge. The research shows why, and what actually works.
        </p>

        <div className={`science-tabs-wrapper ${reveal()}`} style={stagger(2)}>
          <md-tabs ref={tabsRef} id="scienceTabs" aria-label="Research areas">
            <md-primary-tab id="tab-fluency" aria-controls="panel-fluency">
              <md-icon slot="icon">visibility</md-icon>
              The Fluency Illusion
            </md-primary-tab>
            <md-primary-tab id="tab-retrieval" aria-controls="panel-retrieval">
              <md-icon slot="icon">psychology</md-icon>
              Retrieval Practice
            </md-primary-tab>
            <md-primary-tab id="tab-cognitive" aria-controls="panel-cognitive">
              <md-icon slot="icon">memory</md-icon>
              Cognitive Load
            </md-primary-tab>
            <md-primary-tab id="tab-difficulty" aria-controls="panel-difficulty">
              <md-icon slot="icon">fitness_center</md-icon>
              Desirable Difficulty
            </md-primary-tab>
            <md-primary-tab id="tab-adaptation" aria-controls="panel-adaptation">
              <md-icon slot="icon">neurology</md-icon>
              Neurocognitive Adaptation
            </md-primary-tab>
          </md-tabs>

          {/* Panel: The Fluency Illusion */}
          <div className={panelClass('panel-fluency')} id="panel-fluency" role="tabpanel" aria-labelledby="tab-fluency">
            <div className="research-points">
              <div className="research-point">
                <h4>Knowledge Ownership Hierarchy</h4>
                <p>
                  Most learning platforms optimize for recognition, the lowest form of knowledge.
                  True mastery requires the ability to apply knowledge in novel contexts without cues.
                </p>

                <div className="fluency-stepper">
                  <div className="fluency-level" data-level="1">
                    <div className="fluency-level-badge">Level 1</div>
                    <div className="fluency-level-content">
                      <strong>Recognizing</strong>
                      <span>&quot;I&apos;ve seen this&quot;</span>
                      <em>Passive, cue-dependent. Feels like learning but evaporates under pressure.</em>
                    </div>
                  </div>
                  <div className="fluency-connector"></div>
                  <div className="fluency-level" data-level="2">
                    <div className="fluency-level-badge fluency-badge-mid">Level 2</div>
                    <div className="fluency-level-content">
                      <strong>Understanding</strong>
                      <span>&quot;I can explain this&quot;</span>
                      <em>Fragile comprehension. Freezes under pressure or novel contexts.</em>
                    </div>
                  </div>
                  <div className="fluency-connector"></div>
                  <div className="fluency-level" data-level="3">
                    <div className="fluency-level-badge fluency-badge-top">Level 3</div>
                    <div className="fluency-level-content">
                      <strong>Applying</strong>
                      <span>&quot;I can use this in any context&quot;</span>
                      <em>True mastery. Durable, transferable, independent of cues.</em>
                    </div>
                  </div>
                </div>

                <div className="fluency-bar-comparison">
                  <div className="comparison-bar">
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-primary"
                        style={{ '--bar-target': '12%' } as React.CSSProperties}
                      >
                        <span className="bar-label">Platforms reaching Level 3: 12%</span>
                      </div>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-muted"
                        style={{ '--bar-target': '88%' } as React.CSSProperties}
                      >
                        <span className="bar-label">Stuck at Level 1&ndash;2: 88%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="fluency-summary">
                  <strong>Most AI tutors optimize for Level 1. We optimize for Level 3 through structured struggle.</strong>
                </p>

                {/* Cross-Domain Transfer Test */}
                <div className="transfer-test-demo">
                  <h4>Cross-Domain Transfer Test</h4>
                  <p>
                    Level 3 means you can explain a concept outside its original domain.
                    If you truly understand recursion, you can map it to anything.
                  </p>
                  <div className="transfer-mapping">
                    <div className="transfer-from">
                      <span className="material-symbols-outlined">code</span>
                      <strong>Recursion</strong>
                    </div>
                    <div className="transfer-arrow">
                      <span className="material-symbols-outlined">arrow_forward</span>
                    </div>
                    <div className="transfer-to">
                      <span className="material-symbols-outlined">restaurant</span>
                      <strong>Cooking analogy</strong>
                      <em>
                        &quot;A recipe that says &apos;make sauce&apos; but the sauce recipe also says
                        &apos;make sauce.&apos; You need a base case: &apos;use store-bought.&apos;&quot;
                      </em>
                    </div>
                  </div>
                  <p className="transfer-summary">
                    If a learner can generate this analogy unprompted, they own the concept. That&apos;s what we measure.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Panel: Retrieval Practice */}
          <div className={panelClass('panel-retrieval')} id="panel-retrieval" role="tabpanel" aria-labelledby="tab-retrieval">
            <div className="research-points">
              <div className="research-point">
                <h4>Testing Effect</h4>
                <p>
                  Actively recalling information strengthens memory significantly more than re-reading,
                  even when study time is equal.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '61%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Active Recall: 61%</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '40%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Re-reading: 40%</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a href="https://pubmed.ncbi.nlm.nih.gov/16507066/" target="_blank" rel="noopener">
                    Roediger &amp; Karpicke, 2006
                  </a>
                  , 1-week delayed test
                </cite>
              </div>
              <div className="research-point">
                <h4>Spacing Effect</h4>
                <p>
                  Distributed practice over time produces significantly better long-term retention
                  than massed study, confirmed across 259 of 271 experiments.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '74%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Spaced Practice: 74%</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '49%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Massed Study: 49%</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a href="https://pubmed.ncbi.nlm.nih.gov/16719566/" target="_blank" rel="noopener">
                    Cepeda et al., 2006
                  </a>
                  ;{' '}
                  <a
                    href="http://uweb.cas.usf.edu/~drohrer/pdfs/Rohrer&Taylor2007IS.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Rohrer &amp; Taylor, 2007
                  </a>
                </cite>
              </div>
              <div className="research-point">
                <h4>Interleaving</h4>
                <p>
                  Mixing problem types during practice improves transfer and discrimination between concepts.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '89%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Interleaved: 89%</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '60%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Blocked: 60%</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="http://uweb.cas.usf.edu/~drohrer/pdfs/Rohrer&Taylor2007IS.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Rohrer &amp; Taylor, 2007
                  </a>
                  , Experiment 2
                </cite>
              </div>
            </div>
          </div>

          {/* Panel: Cognitive Load */}
          <div className={panelClass('panel-cognitive')} id="panel-cognitive" role="tabpanel" aria-labelledby="tab-cognitive">
            <div className="research-points">
              <div className="research-point">
                <h4>Working Memory Limits</h4>
                <p>
                  Humans can process 4&plusmn;1 chunks of new information simultaneously.
                  Overload causes failure, not learning.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '75%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Optimized Load: higher retention</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '35%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Overloaded: lower retention</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a href="https://pubmed.ncbi.nlm.nih.gov/11515286/" target="_blank" rel="noopener">
                    Cowan, 2001
                  </a>
                  ; Sweller, 2011, illustrative of effect direction
                </cite>
                <div className="how-we-use-this-callout" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--md-sys-color-surface-container-low)', borderRadius: '8px', borderLeft: '4px solid var(--md-sys-color-primary)' }}>
                  <strong>How We Use This:</strong> Scout tracks working memory in real time: 15% decay per minute, +30% reinforcement on correct recall, circuit breaker at 90% saturation.
                </div>
              </div>
              <div className="research-point">
                <h4>Scaffolded Complexity</h4>
                <p>
                  Gradually increasing complexity as expertise grows leads to deeper schema formation.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '75%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Scaffolded: higher transfer</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '40%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Unstructured: lower transfer</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="https://www.uky.edu/~gmswan3/EDC608/Kalyuga2007_Article_ExpertiseReversalEffectAndItsI.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Kalyuga, 2007
                  </a>
                  , effect size d=1.20 for novices
                </cite>
              </div>
              <div className="research-point">
                <h4>Multimedia Principle</h4>
                <p>
                  People learn better from words and pictures together than from words alone,
                  when designed correctly.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '75%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Words + Pictures: higher transfer</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '40%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Words Only: lower transfer</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="https://www.cambridge.org/core/books/multimedia-learning/BFBC13CE39E399E041E1E2AC83A9E392"
                    target="_blank"
                    rel="noopener"
                  >
                    Mayer, 2009
                  </a>
                  , median effect size d=1.39 across 11 studies
                </cite>
              </div>
            </div>
          </div>

          {/* Panel: Desirable Difficulty */}
          <div className={panelClass('panel-difficulty')} id="panel-difficulty" role="tabpanel" aria-labelledby="tab-difficulty">
            <div className="research-points">
              <p className="difficulty-intro">
                AI creates convenience. We create productive friction.{' '}
                <strong>The difficulty IS the learning.</strong>
              </p>
              <div className="research-point">
                <h4>The Misinterpreted-Effort Trap</h4>
                <p>
                  Learners interpret mental effort as a sign of failure, and switch to easier,
                  less effective methods. This is why AI-generated answers feel satisfying but
                  produce no learning. Our platform reframes productive struggle as the signal
                  that real learning is happening.
                </p>
                <cite>
                  <a
                    href="https://bjorklab.psych.ucla.edu/wp-content/uploads/sites/13/2016/04/EBjork_RBjork_2011.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Bjork &amp; Bjork, 2011
                  </a>
                </cite>
              </div>
              <div className="research-point">
                <h4>Productive Struggle</h4>
                <p>
                  Learning that feels effortful (but achievable) creates stronger, more durable
                  neural connections.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '65%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Effortful retrieval: higher retention</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '40%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Passive review: lower retention</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="https://bjorklab.psych.ucla.edu/wp-content/uploads/sites/13/2016/04/EBjork_RBjork_2011.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Bjork &amp; Bjork, 2011
                  </a>
                  , desirable difficulties framework
                </cite>
              </div>
              <div className="research-point">
                <h4>Generation Effect</h4>
                <p>
                  Generating answers (even incorrect ones) before receiving instruction boosts
                  subsequent learning.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '75%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Generate first: higher retention</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '50%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Receive first: lower retention</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="https://learninglab.uchicago.edu/Pre-Testing_files/RichlandKornellKao.pdf"
                    target="_blank"
                    rel="noopener"
                  >
                    Richland, Kornell &amp; Kao, 2009
                  </a>
                  , pre-testing boosts subsequent learning
                </cite>
              </div>
              <div className="research-point">
                <h4>Error-Driven Learning</h4>
                <p>
                  Errors followed by corrective feedback create prediction-error signals that
                  accelerate memory consolidation. Immediate error correction reduces retention. Scout waits 2000ms before intervening &mdash; matching FSI Protocol research on optimal correction timing.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '65%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Error + feedback: higher retention</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '48%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Error-free study: lower retention</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a
                    href="https://doi.org/10.1146/annurev-psych-010416-044022"
                    target="_blank"
                    rel="noopener"
                  >
                    Metcalfe, 2017
                  </a>
                  , Annual Review of Psychology
                </cite>
              </div>
            </div>
          </div>

          {/* Panel: Neurocognitive Adaptation */}
          <div className={panelClass('panel-adaptation')} id="panel-adaptation" role="tabpanel" aria-labelledby="tab-adaptation">
            <div className="research-points">
              <div className="research-point">
                <h4>Working Memory Limits by Neurotype</h4>
                <p>
                  Working memory capacity is not universal. It varies significantly across neurotypes, requiring adapted chunking strategies.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '100%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Neurotypical: 7 items</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '71%' } as React.CSSProperties}
                    >
                      <span className="bar-label">ADHD: 5 items | Dyslexia: 6 items</span>
                    </div>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-muted"
                      style={{ '--bar-target': '57%' } as React.CSSProperties}
                    >
                      <span className="bar-label">Combined: 4 items</span>
                    </div>
                  </div>
                </div>
                <cite>
                  <a href="https://pubmed.ncbi.nlm.nih.gov/11515286/" target="_blank" rel="noopener">Cowan, 2001</a>; Miller, 1956
                </cite>
              </div>
              <div className="research-point">
                <h4>Reward Sensitivity &amp; Immediate Feedback</h4>
                <p>
                  ADHD brains prefer immediate small rewards over delayed large ones, making rapid, tight feedback loops essential for sustained engagement.
                </p>
                <cite>
                  Sonuga-Barke, 2005
                </cite>
              </div>
              <div className="research-point">
                <h4>Visual Crowding &amp; Letter Spacing</h4>
                <p>
                  Extra-large letter spacing (0.1em) mitigates visual crowding, yielding a significant accuracy improvement for dyslexic readers.
                </p>
                <div className="comparison-bar">
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-primary"
                      style={{ '--bar-target': '20%' } as React.CSSProperties}
                    >
                      <span className="bar-label">20% accuracy improvement with 0.1em spacing</span>
                    </div>
                  </div>
                </div>
                <cite>
                  Zorzi et al., 2012
                </cite>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
